<template>
  <section class="d-grid gap-3">
    <header class="d-flex justify-content-between align-items-start flex-wrap gap-3">
      <div>
        <h2 class="h5 mb-1">Ventas por período</h2>
        <p class="text-muted small mb-0">Desglose por método de pago, cajero, caja y sucursal.</p>
      </div>
      <button class="btn btn-outline-dark btn-sm" :disabled="loading || !filas.length" @click="exportar">
        <i class="bi bi-download me-1"></i>Exportar CSV
      </button>
    </header>

    <div class="card shadow-sm">
      <div class="card-body">
        <div class="row g-2 align-items-end">
          <div class="col-auto">
            <label class="form-label small mb-1">Desde</label>
            <input v-model="filtros.fecha_desde" type="date" class="form-control form-control-sm" />
          </div>
          <div class="col-auto">
            <label class="form-label small mb-1">Hasta</label>
            <input v-model="filtros.fecha_hasta" type="date" class="form-control form-control-sm" />
          </div>
          <div class="col-auto">
            <label class="form-label small mb-1">Sucursal</label>
            <input v-model="filtros.sucursal_id" type="number" min="1" class="form-control form-control-sm" placeholder="Todas" style="width: 110px" />
          </div>
          <div class="col-auto">
            <label class="form-label small mb-1">Caja</label>
            <input v-model="filtros.caja_id" type="number" min="1" class="form-control form-control-sm" placeholder="Todas" style="width: 110px" />
          </div>
          <div class="col-auto">
            <button class="btn btn-dark btn-sm" :disabled="loading" @click="cargar">
              {{ loading ? 'Buscando...' : 'Buscar' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger mb-0">{{ error }}</div>

    <div v-if="totales" class="row g-3">
      <div class="col-md-3">
        <div class="card shadow-sm"><div class="card-body">
          <div class="text-muted small">Ventas</div>
          <div class="h5 mb-0">{{ totales.cantidad_ventas }}</div>
        </div></div>
      </div>
      <div class="col-md-3">
        <div class="card shadow-sm"><div class="card-body">
          <div class="text-muted small">Monto total</div>
          <div class="h5 mb-0">{{ formatoMonto(totales.monto_total) }}</div>
        </div></div>
      </div>
      <div class="col-md-3">
        <div class="card shadow-sm"><div class="card-body">
          <div class="text-muted small">IVA total</div>
          <div class="h5 mb-0">{{ formatoMonto(totales.iva_total) }}</div>
        </div></div>
      </div>
      <div class="col-md-3">
        <div class="card shadow-sm"><div class="card-body">
          <div class="text-muted small mb-1">Por método</div>
          <div v-for="m in totales.por_metodo_pago" :key="m.metodo_pago" class="small">
            {{ m.metodo_pago }}: {{ formatoMonto(m.total) }}
          </div>
        </div></div>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="table-responsive">
        <table class="table align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th>Comprobante</th><th>Fecha</th><th>Cajero</th><th>Caja</th><th>Sucursal</th><th>Forma</th><th class="text-end">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading"><td colspan="7" class="text-center py-4">Cargando...</td></tr>
            <tr v-else-if="!filas.length"><td colspan="7" class="text-center py-4 text-muted">Sin resultados</td></tr>
            <tr v-else v-for="f in filas" :key="f.nro_comprobante">
              <td>{{ f.nro_comprobante }}</td>
              <td>{{ formatoFecha(f.fecha_emision) }}</td>
              <td>{{ f.cajero_nombre || '-' }}</td>
              <td>{{ f.caja_id }}</td>
              <td>{{ f.sucursal_id }}</td>
              <td>{{ f.forma_operacion }}</td>
              <td class="text-end">{{ formatoMonto(f.total_iva_incluido) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import reportsService from '@/services/reportsService'

const route = useRoute()
const hoy = new Date().toISOString().slice(0, 10)
const inicioMes = `${hoy.slice(0, 7)}-01`

// Deep-link desde una tarjeta del dashboard (DASH-4A-001): si llega con
// fecha_desde/fecha_hasta en la query, precarga el filtro con eso en vez
// del default de "este mes".
const filtros = ref({
  fecha_desde: route.query.fecha_desde || inicioMes,
  fecha_hasta: route.query.fecha_hasta || hoy,
  sucursal_id: '',
  caja_id: '',
})
const filas = ref([])
const totales = ref(null)
const loading = ref(false)
const error = ref('')

function formatoMonto(valor) {
  return new Intl.NumberFormat('es-PY', { maximumFractionDigits: 0 }).format(Number(valor) || 0)
}
function formatoFecha(valor) {
  return valor ? new Date(valor).toLocaleString('es-PY') : '-'
}

async function cargar() {
  loading.value = true
  error.value = ''
  try {
    const data = await reportsService.getVentas(filtros.value)
    filas.value = data.filas
    totales.value = data.totales
  } catch (e) {
    error.value = e?.response?.data?.message || 'No se pudo cargar el reporte.'
  } finally {
    loading.value = false
  }
}

async function exportar() {
  try {
    await reportsService.descargarCsv('ventas', filtros.value, 'ventas')
  } catch (e) {
    error.value = 'No se pudo exportar el CSV.'
  }
}

onMounted(cargar)
</script>
